import React from "react";
import { Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { GlobalStyles } from "../style/Style";
import { HorseRacingPrompt, HorseRaceWagerType } from "@freeplay/shared-code";
import { SimplePropButtonDisplay } from "./SimplePropButtonDisplay";

export interface SimpleHorseRacingPropProps {
    propType: HorseRaceWagerType;
    propValue: HorseRacingPrompt;
    currentPick?: number;
    winIndex: number | null;
}

export const SimpleHorseRacingOutcomeDisplay = ({
    propType,
    propValue,
    currentPick,
    winIndex,
}: SimpleHorseRacingPropProps): JSX.Element => {
    const styles = useDynamicStyles(generateStyles);

    let buttons: JSX.Element;

    if (propType === HorseRaceWagerType.HORSETOWIN || propType === HorseRaceWagerType.TOP4) {
        buttons = (
            <View style={styles.buttonContainer}>
                <SimplePropButtonDisplay
                    buttonText={"Yes"}
                    width={100}
                    isActive={currentPick === 1}
                    index={1}
                    resulted={winIndex !== null}
                    isWinner={currentPick === winIndex}
                    winIndex={winIndex}
                />
                <SimplePropButtonDisplay
                    buttonText={"No"}
                    width={100}
                    isActive={currentPick === 0}
                    index={0}
                    resulted={winIndex !== null}
                    isWinner={currentPick === winIndex}
                    winIndex={winIndex}
                />
            </View>
        );
    } else {
        buttons = (
            <View style={styles.buttonContainer}>
                <SimplePropButtonDisplay
                    buttonText={"Over"}
                    width={80}
                    isActive={currentPick === 1}
                    index={1}
                    resulted={winIndex !== null}
                    isWinner={currentPick === winIndex}
                    winIndex={winIndex}
                />
                <SimplePropButtonDisplay
                    buttonText={"Under"}
                    width={80}
                    isActive={currentPick === -1}
                    index={-1}
                    resulted={winIndex !== null}
                    isWinner={currentPick === winIndex}
                    winIndex={winIndex}
                />
                <SimplePropButtonDisplay
                    buttonText={"Exactly"}
                    width={80}
                    isActive={currentPick === 0}
                    index={0}
                    resulted={winIndex !== null}
                    isWinner={currentPick === winIndex}
                    winIndex={winIndex}
                />
            </View>
        );
    }

    const getPropString = () => {
        switch (propType) {
            case HorseRaceWagerType.HORSETOWIN: {
                return (
                    <Text style={styles.propString}>
                        Will{" "}
                        <Text style={styles.propValue}>
                            {
                                propValue.horseRaceEvent.horseData.find(
                                    (d) => d.programNumber === propValue.horseProgramNumber,
                                )?.horseName
                            }
                        </Text>{" "}
                        win?
                    </Text>
                );
            }
            case HorseRaceWagerType.TOP4: {
                return (
                    <Text style={styles.propString}>
                        Will{" "}
                        <Text style={styles.propValue}>
                            {
                                propValue.horseRaceEvent.horseData.find(
                                    (d) => d.programNumber === propValue.horseProgramNumber,
                                )?.horseName
                            }
                        </Text>{" "}
                        finish in the top 4?
                    </Text>
                );
            }
            case HorseRaceWagerType.WINNINGTIME: {
                return (
                    <Text style={styles.propString}>
                        Winning time: <Text style={styles.propValue}>{propValue.winningTime}</Text>
                    </Text>
                );
            }
            case HorseRaceWagerType.WINNINGDISTANCE: {
                return (
                    <Text style={styles.propString}>
                        Winning distance: <Text style={styles.propValue}>{propValue.winningDistance}</Text>
                    </Text>
                );
            }
        }
    };

    return (
        <View style={[styles.prop]}>
            {getPropString()}
            {buttons}
        </View>
    );
};

const generateStyles = () => {
    return EStyleSheet.create({
        prop: {
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
        },
        propString: {
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            fontWeight: "700",
            fontSize: 12,
            marginTop: 10,
        },
        propValue: {
            color: () => EStyleSheet.value(GlobalStyles.ACCENT_COLOUR),
        },
        buttonContainer: {
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            marginTop: 5,
        },
    });
};
